import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import {
  fetchReferralStatusOptions,
  createReferralStatusOption,
  updateReferralStatusOption,
  deleteReferralStatusOption,
} from "../api/api";

const EMPTY_FORM = { name: "", is_active: true, is_default: false };

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function ReferralStatuses() {

  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [rowBusy, setRowBusy] = useState(null);
  const [rowError, setRowError] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchReferralStatusOptions();
      setStatuses(data);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.name.trim()) {
      setFormError("Status name is required.");
      return;
    }

    setSubmitting(true);
    try {
      await createReferralStatusOption({
        name: form.name.trim(),
        is_active: form.is_active,
        is_default: form.is_default,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, is_active: s.is_active, is_default: s.is_default });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const saveEdit = async (statusId) => {
    if (!editForm.name.trim()) {
      setRowError("Status name is required.");
      return;
    }

    setRowBusy(statusId);
    setRowError("");
    try {
      await updateReferralStatusOption(statusId, {
        name: editForm.name.trim(),
        is_active: editForm.is_active,
        is_default: editForm.is_default,
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete status "${s.name}"?`)) return;

    setRowBusy(s.id);
    setRowError("");
    try {
      await deleteReferralStatusOption(s.id);
      setStatuses((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      setRowError(errorMessage(err));
    } finally {
      setRowBusy(null);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 style={{ marginTop: "0", marginBottom: 0 }}>Referral Statuses</h1>
          <button className="login-btn" style={{ width: "auto", padding: "10px 20px" }} onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? "Cancel" : "+ Add Status"}
          </button>
        </div>

        <p style={{ color: "#666" }}>
          The pipeline stages available when admin updates a referral's status (e.g. Interested, Site Visited, Purchased). Add your own, or deactivate ones you no longer use.
        </p>

        {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}
        {rowError && <p className="login-error" style={{ color: "red" }}>{rowError}</p>}

        {showForm && (
          <div className="section-card">
            <h3>New Status</h3>

            {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}

            <form onSubmit={handleCreate}>
              <input
                type="text"
                name="name"
                placeholder="Status Name *"
                value={form.name}
                onChange={handleFormChange}
                required
              />

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleFormChange} />
                Active
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="checkbox" name="is_default" checked={form.is_default} onChange={handleFormChange} />
                Default for new referrals
              </label>

              <button type="submit" className="login-btn" style={{ width: "auto", padding: "8px 16px" }} disabled={submitting}>
                {submitting ? "Creating…" : "Create Status"}
              </button>
            </form>
          </div>
        )}

        {loading && <p>Loading statuses…</p>}

        {!loading && (
          <div style={{ background: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>

            <table className="plot-table" style={{ width: "100%", borderCollapse: "collapse" }}>

              <thead style={{ background: "#f5f5f5" }}>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Default</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {statuses.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: "15px" }}>No referral statuses yet.</td></tr>
                )}

                {statuses.map((s) => (
                  <tr key={s.id}>
                    {editingId === s.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            name="name"
                            value={editForm.name}
                            onChange={handleEditFormChange}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <input type="checkbox" name="is_active" checked={editForm.is_active} onChange={handleEditFormChange} />
                            Active
                          </label>
                        </td>
                        <td>
                          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <input type="checkbox" name="is_default" checked={editForm.is_default} onChange={handleEditFormChange} />
                            Default
                          </label>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              title="Save"
                              onClick={() => saveEdit(s.id)}
                              disabled={rowBusy === s.id}
                              style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={cancelEdit}
                              disabled={rowBusy === s.id}
                              style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td><b>{s.name}</b></td>
                        <td>{s.is_active ? "✅ Active" : "🚫 Inactive"}</td>
                        <td>{s.is_default ? "⭐ Default" : "—"}</td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button className="view-btn" onClick={() => startEdit(s)} disabled={rowBusy === s.id}>
                              Edit
                            </button>
                            <button
                              className="view-btn"
                              onClick={() => handleDelete(s)}
                              disabled={rowBusy === s.id}
                              style={{ background: "#dc2626", color: "white" }}
                            >
                              {rowBusy === s.id ? "…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

              </tbody>

            </table>

          </div>
        )}

      </div>

    </div>
  );
}

export default ReferralStatuses;
